import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../store/authStore'
import { useToastStore } from '../../store/toastStore'
import {
  SUPPORTED_LOCALES,
  LOCALES,
  isSupportedLocale,
  DEFAULT_LOCALE,
  type Locale,
} from '../../i18n/locales'
import { translateApiError } from '../../lib/apiErrors'

export default function LanguageSelector() {
  const { i18n, t } = useTranslation('common')
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { addToast } = useToastStore()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const current = isSupportedLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const handleChange = async (next: Locale) => {
    if (next === current) {
      setOpen(false)
      return
    }

    if (isAuthenticated) {
      try {
        await useAuthStore.getState().updateLanguage(next)
      } catch (err) {
        addToast(translateApiError(err, t), 'error')
      }
    } else {
      i18n.changeLanguage(next)
      localStorage.setItem('i18nextLng', next)
    }
    setOpen(false)
  }

  return (
    <div
      ref={rootRef}
      className="relative inline-flex text-sm"
      title={t('language.select')}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-md border border-[#212121] bg-[#0d0d0d] px-2.5 py-1 font-medium text-gray-300 hover:bg-[#1a1a1a] hover:text-white transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {current.toUpperCase()}
        <span className="text-gray-500" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-full z-50 mt-1 min-w-[10rem] overflow-hidden rounded-md border border-[#212121] bg-[#0d0d0d] shadow-lg"
        >
          {SUPPORTED_LOCALES.map((locale) => (
            <li key={locale} role="option" aria-selected={current === locale}>
              <button
                type="button"
                onClick={() => handleChange(locale)}
                className={[
                  'flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors',
                  current === locale
                    ? 'bg-[#212121] text-white'
                    : 'text-gray-300 hover:bg-[#1a1a1a] hover:text-white',
                ].join(' ')}
              >
                <span>{LOCALES[locale].nativeName}</span>
                <span className="text-xs text-gray-500">{locale.toUpperCase()}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
